import { useRef, useEffect, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

export default function FinalCTA() {
  const { ref, inView } = useInView();
  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="bg-[#0A0A0A] py-28 md:py-36 relative overflow-hidden" ref={ref}>
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: 'linear-gradient(#C9A84C 1px, transparent 1px), linear-gradient(90deg, #C9A84C 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      ></div>
      {/* Gold vertical accent lines */}
      <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-[#C9A84C]/30 to-transparent hidden lg:block"></div>
      <div className="absolute right-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-[#C9A84C]/30 to-transparent hidden lg:block"></div>

      <div className="relative z-10 max-w-[860px] mx-auto px-6 md:px-12 text-center">
        <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-4 mb-7">
            <div className="w-12 h-[1px] bg-[#C9A84C]/50"></div>
            <span className="text-[#C9A84C] text-[10px] tracking-[0.5em] uppercase font-medium">Take Action Now</span>
            <div className="w-12 h-[1px] bg-[#C9A84C]/50"></div>
          </div>
          <h2 className="font-serif text-white text-4xl md:text-6xl font-bold mb-6 leading-[1.08]">
            Protect Your Position.<br />
            <span className="text-[#C9A84C]">Strengthen Your Case.</span>
          </h2>
          <p className="text-white/50 text-lg max-w-xl mx-auto mb-14 leading-relaxed">
            Delaying legal action can limit your options. Get clarity and a strategy built to move forward with confidence — starting today.
          </p>

          {/* Trust signals */}
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-12">
            {[
              { icon: 'ri-star-fill', text: '4.8 Rating' },
              { icon: 'ri-shield-check-fill', text: 'Martindale-Hubbell Distinguished' },
              { icon: 'ri-time-fill', text: 'Free Consultation' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-[#C9A84C] text-sm`}></i>
                </div>
                <span className="text-white/40 text-[12px] tracking-wider">{item.text}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToContact}
              className="px-12 py-5 bg-[#C9A84C] text-black font-bold text-[12px] tracking-[0.28em] uppercase hover:bg-[#b8943e] active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Speak With an Attorney Today
            </button>
            <a
              href="tel:8059228308"
              className="px-12 py-5 border-2 border-[#C9A84C]/50 text-[#C9A84C] font-bold text-[12px] tracking-[0.28em] uppercase hover:bg-[#C9A84C] hover:text-black active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2.5"
            >
              <i className="ri-phone-fill text-sm"></i>
              Call (805) 922-8308
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
